import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../lib/api';

// ── Customer Synopsis ──
//
// The 360° view staff pull up when a customer calls: an AI-written summary of
// what they buy, hard numbers (health, cadence, spend trend, top products),
// recent orders, and staff notes (delivery / technician / general). Rendered
// in place of the Customers tab list, same swap pattern as OrderGuideEditor.
//
// Notes are writable by every portal persona — this is the one write
// technicians and delivery staff get (enforced server-side via the
// customer-notes area in staff-roles.js). Delete is author-or-admin only.
//
// Two homes (docs/site-map.md): Manage → Customers shows only the notes
// (delivery instructions, machine issues); the sales site's Customer 360 shows
// the AI summary and the notes, since it already has the numbers and orders.
// `api` lets each site call through its own login.

const ADMIN_SYNOPSIS_API = {
  get: (customerId) => adminApi.getCustomerSynopsis(customerId),
  addNote: (customerId, note) => adminApi.addCustomerNote(customerId, note),
  deleteNote: (customerId, noteId) => adminApi.deleteCustomerNote(customerId, noteId),
};
const ALL_SECTIONS = ['summary', 'health', 'notes', 'products', 'orders'];

const NOTE_TYPE_META = {
  general:    { label: 'General',    color: '#546e7a', bg: '#eceff1' },
  delivery:   { label: 'Delivery',   color: '#1565c0', bg: '#e3f2fd' },
  technician: { label: 'Technician', color: '#6a1b9a', bg: '#f3e5f5' },
};

const SYNOPSIS_FALLBACKS = {
  no_api_key: 'AI summary not configured yet — the numbers below are live.',
  no_orders: 'No order history yet, so there is nothing to summarize.',
  generation_failed: 'AI summary is temporarily unavailable — the numbers below are live.',
};

const STATUS_META = {
  active:        { label: 'Active',    color: '#27ae60' },
  dormant:       { label: 'Dormant',   color: '#e67e22' },
  never_ordered: { label: 'No Orders', color: '#95a5a6' },
};

function fmt$(v) { return '$' + (v || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
function fmtDate(d) { if (!d) return '—'; return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }

function defaultNoteType(role) {
  if (role === 'delivery') return 'delivery';
  if (role === 'technician') return 'technician';
  return 'general';
}

export default function CustomerSynopsis({ customer, me, onBack, api = ADMIN_SYNOPSIS_API, sections = ALL_SECTIONS }) {
  const show = (k) => sections.includes(k);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState(defaultNoteType(me?.role));
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      setData(await api.get(customer.id));
    } catch (e) {
      setError(e.message || 'Failed to load synopsis');
    } finally {
      setLoading(false);
    }
  }, [customer.id, api]);

  useEffect(() => { load(); }, [load]);

  async function addNote() {
    const text = noteText.trim();
    if (!text || noteBusy) return;
    setNoteBusy(true);
    setNoteError('');
    try {
      await api.addNote(customer.id, { text, type: noteType });
      setNoteText('');
      await load();
    } catch (e) {
      setNoteError(e.message || 'Failed to add note');
    } finally {
      setNoteBusy(false);
    }
  }

  async function deleteNote(noteId) {
    if (!window.confirm('Delete this note?')) return;
    setNoteError('');
    try {
      await api.deleteNote(customer.id, noteId);
      await load();
    } catch (e) {
      setNoteError(e.message || 'Failed to delete note');
    }
  }

  const isAdmin = me?.role === 'owner' || me?.role === 'silver_service_admin';
  const canDelete = (note) => isAdmin || (!!me?.email && note.authorEmail === me.email);

  const insights = data?.insights;
  const synopsis = data?.synopsis;
  const notes = data?.notes || [];
  const status = STATUS_META[insights?.status] || STATUS_META.never_ordered;
  const trendArrow = insights?.trend?.direction === 'up' ? '▲' : insights?.trend?.direction === 'down' ? '▼' : '▬';
  const trendColor = insights?.trend?.direction === 'up' ? '#27ae60' : insights?.trend?.direction === 'down' ? '#e74c3c' : '#888';

  return (
    <div>
      {onBack && <div style={s.header}>
        <button style={s.backBtn} onClick={onBack}>← Back to Customers</button>
        <div>
          <h2 style={s.title}>{data?.customer?.name || customer.name}</h2>
          <div style={s.contact}>
            {(data?.customer?.email || customer.email) || 'No email'}
            {(data?.customer?.phone || customer.phone) ? ` · ${data?.customer?.phone || customer.phone}` : ''}
          </div>
        </div>
      </div>}

      {loading && <p style={s.sub}>Loading{show('summary') ? ' synopsis... (first view can take a few seconds while the AI summary is written)' : ' notes...'}</p>}
      {error && <p style={s.errorText}>{error}</p>}

      {!loading && !error && data && (
        <>
          {/* AI summary */}
          {show('summary') && <div style={s.aiCard}>
            <div style={s.aiLabel}>
              AI summary{synopsis?.generatedAt ? ` · generated ${fmtDate(synopsis.generatedAt)}` : ''}
            </div>
            {synopsis?.text
              ? <div style={s.aiText}>{synopsis.text}</div>
              : <div style={s.aiFallback}>{SYNOPSIS_FALLBACKS[synopsis?.reason] || SYNOPSIS_FALLBACKS.generation_failed}</div>}
          </div>}

          {/* Health KPIs */}
          {show('health') && <div style={s.kpiGrid}>
            <div style={s.kpiCard}>
              <div style={s.kpiLabel}>Status</div>
              <div style={{ ...s.kpiValue, color: status.color }}>{status.label}</div>
            </div>
            <div style={s.kpiCard}>
              <div style={s.kpiLabel}>Last Order</div>
              <div style={s.kpiValue}>{fmtDate(insights.lastOrderDate)}</div>
              {insights.daysSinceLastOrder != null && <div style={s.kpiHint}>{insights.daysSinceLastOrder} days ago</div>}
            </div>
            <div style={s.kpiCard}>
              <div style={s.kpiLabel}>Order Cadence</div>
              <div style={s.kpiValue}>{insights.cadenceDays != null ? `~${Math.round(insights.cadenceDays)} days` : '—'}</div>
              <div style={s.kpiHint}>{insights.orderCount} orders on record</div>
            </div>
            <div style={s.kpiCard}>
              <div style={s.kpiLabel}>Avg Order Value</div>
              <div style={s.kpiValue}>{fmt$(insights.avgOrderValue)}</div>
            </div>
            <div style={s.kpiCard}>
              <div style={s.kpiLabel}>30-Day Trend</div>
              <div style={{ ...s.kpiValue, color: trendColor }}>{trendArrow} {fmt$(insights.trend.last30)}</div>
              <div style={s.kpiHint}>vs {fmt$(insights.trend.prior60 / 2)}/30d before</div>
            </div>
          </div>}

          {/* Notes — above the order tables so the human context (delivery
              instructions, machine issues) is in reach right after the KPIs */}
          {show('notes') && <div style={s.section}>
            <div style={s.sectionTitle}>Notes ({notes.length})</div>
            <div style={s.noteForm}>
              <div style={s.noteTypeBar}>
                {Object.entries(NOTE_TYPE_META).map(([key, meta]) => (
                  <button
                    key={key}
                    style={noteType === key
                      ? { ...s.noteTypeBtn, background: meta.bg, color: meta.color, border: `1px solid ${meta.color}` }
                      : s.noteTypeBtn}
                    onClick={() => setNoteType(key)}
                  >
                    {meta.label}
                  </button>
                ))}
              </div>
              <textarea
                style={s.noteInput}
                rows={3}
                maxLength={2000}
                placeholder="Add a note — e.g. delivery instructions, machine issues, preferences..."
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
              />
              <div style={s.noteFormFooter}>
                {noteError && <span style={s.errorText}>{noteError}</span>}
                <button style={s.addNoteBtn} onClick={addNote} disabled={noteBusy || !noteText.trim()}>
                  {noteBusy ? 'Saving...' : 'Add Note'}
                </button>
              </div>
            </div>

            {notes.length === 0 && <p style={s.sub}>No notes yet — be the first to add one.</p>}
            {notes.map(n => {
              const meta = NOTE_TYPE_META[n.type] || NOTE_TYPE_META.general;
              return (
                <div key={n.noteId} style={s.noteCard}>
                  <div style={s.noteHeader}>
                    <span style={{ ...s.noteBadge, background: meta.bg, color: meta.color }}>{meta.label}</span>
                    <span style={s.noteMeta}>{n.authorName || n.authorEmail || 'Staff'} · {fmtDate(n.createdAt)}</span>
                    {canDelete(n) && (
                      <button style={s.noteDelete} title="Delete note" onClick={() => deleteNote(n.noteId)}>×</button>
                    )}
                  </div>
                  <div style={s.noteText}>{n.text}</div>
                </div>
              );
            })}
          </div>}

          {/* Top products */}
          {show('products') && <div style={s.section}>
            <div style={s.sectionTitle}>
              Most Ordered Products
              {insights.sampled && <span style={s.sectionHint}> · based on the last 200 orders</span>}
            </div>
            {insights.topProducts.length === 0 ? (
              <p style={s.sub}>No order history yet.</p>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Product</th><th style={s.th}>SKU</th>
                    <th style={s.thR}>Orders</th><th style={s.thR}>Units</th><th style={s.thR}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {insights.topProducts.map((p, i) => (
                    <tr key={p.itemId || p.itemName || i}>
                      <td style={s.td}>{p.itemName}</td>
                      <td style={s.td}>{p.sku || '—'}</td>
                      <td style={s.tdR}>{p.orderLineCount}</td>
                      <td style={s.tdR}>{p.totalQuantity}</td>
                      <td style={s.tdR}>{fmt$(p.totalAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>}

          {/* Recent orders */}
          {show('orders') && <div style={s.section}>
            <div style={s.sectionTitle}>Recent Orders</div>
            {data.recentOrders.length === 0 ? (
              <p style={s.sub}>No orders yet.</p>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr><th style={s.th}>Order #</th><th style={s.th}>Date</th><th style={s.th}>Status</th><th style={s.thR}>Total</th></tr>
                </thead>
                <tbody>
                  {data.recentOrders.map(o => (
                    <tr key={o.id}>
                      <td style={s.td}>{o.number}</td>
                      <td style={s.td}>{fmtDate(o.date)}</td>
                      <td style={s.td}>{o.status}</td>
                      <td style={s.tdR}>{fmt$(o.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>}

        </>
      )}
    </div>
  );
}

const s = {
  header: { display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' },
  backBtn: { background: 'none', border: '1px solid #A50B18', borderRadius: 8, padding: '0.5rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#A50B18', whiteSpace: 'nowrap' },
  title: { margin: 0, fontSize: '1.3rem' },
  contact: { fontSize: '0.85rem', color: '#777', marginTop: '0.15rem' },
  sub: { color: '#888', fontSize: '0.9rem' },
  errorText: { color: '#e74c3c', fontSize: '0.85rem' },

  aiCard: { background: '#fdf8f0', border: '1px solid #f0e2c8', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1rem' },
  aiLabel: { fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#a07a2c', marginBottom: '0.4rem' },
  aiText: { fontSize: '0.95rem', lineHeight: 1.55, color: '#3d3428' },
  aiFallback: { fontSize: '0.88rem', color: '#999', fontStyle: 'italic' },

  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' },
  kpiCard: { background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '0.85rem 1rem' },
  kpiLabel: { fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.03em' },
  kpiValue: { fontSize: '1.15rem', fontWeight: 700, marginTop: '0.25rem' },
  kpiHint: { fontSize: '0.75rem', color: '#999', marginTop: '0.15rem' },

  section: { marginBottom: '1.5rem' },
  sectionTitle: { fontSize: '0.95rem', fontWeight: 700, marginBottom: '0.6rem' },
  sectionHint: { fontSize: '0.78rem', fontWeight: 400, color: '#999' },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', background: '#fff', border: '1px solid #eee', borderRadius: 8 },
  th: { textAlign: 'left', padding: '0.5rem 0.75rem', borderBottom: '1px solid #eee', color: '#777', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' },
  thR: { textAlign: 'right', padding: '0.5rem 0.75rem', borderBottom: '1px solid #eee', color: '#777', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' },
  td: { padding: '0.5rem 0.75rem', borderBottom: '1px solid #f5f5f5' },
  tdR: { padding: '0.5rem 0.75rem', borderBottom: '1px solid #f5f5f5', textAlign: 'right' },

  noteForm: { background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 10, padding: '0.85rem', marginBottom: '0.9rem' },
  noteTypeBar: { display: 'flex', gap: '0.4rem', marginBottom: '0.5rem' },
  noteTypeBtn: { border: '1px solid #ddd', borderRadius: 20, background: '#fff', color: '#777', padding: '0.3rem 0.85rem', fontSize: '0.78rem', cursor: 'pointer' },
  noteInput: { width: '100%', boxSizing: 'border-box', border: '1px solid #ddd', borderRadius: 8, padding: '0.6rem', fontSize: '0.88rem', fontFamily: 'inherit', resize: 'vertical' },
  noteFormFooter: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' },
  addNoteBtn: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 8, padding: '0.5rem 1.1rem', fontSize: '0.85rem', cursor: 'pointer' },

  noteCard: { background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '0.75rem 0.9rem', marginBottom: '0.6rem' },
  noteHeader: { display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.35rem' },
  noteBadge: { borderRadius: 20, padding: '0.15rem 0.6rem', fontSize: '0.7rem', fontWeight: 700 },
  noteMeta: { fontSize: '0.75rem', color: '#999', flex: 1 },
  noteDelete: { background: 'none', border: 'none', color: '#bbb', fontSize: '1.1rem', cursor: 'pointer', padding: '0 0.25rem', lineHeight: 1 },
  noteText: { fontSize: '0.88rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', color: '#333' },
};
